import '@/assets/styles/components/verification-code.styles.scss';
import React, { useState, useEffect, useRef, Fragment } from 'react';
import { CircleAlert, Clock } from 'lucide-react';
import clsx from 'clsx';
import { useMutationProvider } from '@/hooks';
import { useApi } from '@/api/useApi.tsx';

interface VerificationCodeProps {
	phone: string;
	onNext: (data: any) => void;
	onProgress?: (code: string[]) => void;
	verificationCodeStatusIsOk?: boolean;
	disabled: boolean;
}

export const VerificationCode: React.FC<VerificationCodeProps> = ({ phone, onNext, onProgress, verificationCodeStatusIsOk, disabled }) => {
	const { VerificationControllerApi } = useApi();

	const sendVerificationCode = useMutationProvider(() => VerificationControllerApi.sendVerificationCode(phone), {
		onSuccess: () => {
			setTimeLeft(179);
			setCode(['', '', '', '']);
			setIsActiveResend(true);
		}
	});

	const [code, setCode] = useState(['', '', '', '']);
	const [isActiveResend, setIsActiveResend] = useState(true);
	const [timeLeft, setTimeLeft] = useState(179);
	const inputRefs = [useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null), useRef<HTMLInputElement>(null)];

	useEffect(() => {
		const fixPagePosition = () => {
			const bodyStyle = document.body.style;

			const height = window.innerHeight;
			const width = window.innerWidth;

			bodyStyle.position = 'fixed';
			bodyStyle.width = width + 'px';
			bodyStyle.height = height + 'px';
			bodyStyle.overflow = 'hidden';

			return () => {
				bodyStyle.position = '';
				bodyStyle.width = '';
				bodyStyle.height = '';
				bodyStyle.overflow = '';
			};
		};

		const styleInputs = () => {
			const codeInputs = document.querySelector('.code-inputs');
			if (codeInputs) {
				const style = document.createElement('style');
				style.innerHTML = `
          .code-inputs {
            position: relative;
            z-index: 999;
            transform: translateZ(0);
            will-change: transform;
          }
          .code-inputs input {
            transform: translateZ(0);
            will-change: transform;
          }
          .verification-section {
            position: relative;
            z-index: 100;
            transform: translateZ(0);
            will-change: transform;
          }
        `;
				document.head.appendChild(style);

				return () => {
					document.head.removeChild(style);
				};
			}
			return () => {};
		};

		const cleanup2 = fixPagePosition();
		const cleanup3 = styleInputs();

		if (inputRefs[0].current) {
			inputRefs[0].current.focus();
		}

		const timer = setInterval(() => {
			setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
		}, 1000);

		return () => {
			clearInterval(timer);
			cleanup2();
			cleanup3();
		};
	}, []);

	const resendCode = async () => {
		if (!isActiveResend) {
			return;
		}

		setIsActiveResend(false);
		await sendVerificationCode.mutateAsync();
	};

	const handleInput = (index: number, value: string) => {
		if (/^\d*$/.test(value) && value.length <= 1) {
			const newCode = [...code];
			newCode[index] = value;
			setCode(newCode);
			onProgress?.(newCode);

			if (value && index < 3 && inputRefs[index + 1].current) {
				setTimeout(() => {
					if (inputRefs[index + 1].current) {
						inputRefs[index + 1].current.focus();
					}
				}, 0);
			}
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();

		const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').substring(0, 4);

		if (pasteData) {
			const newCode = [...code];

			for (let i = 0; i < Math.min(pasteData.length, 4); i++) {
				newCode[i] = pasteData[i];
			}

			setCode(newCode);
			onProgress?.(newCode);

			if (inputRefs[Math.min(pasteData.length, 3)].current) {
				inputRefs[Math.min(pasteData.length, 3)].current?.focus();
			}
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' && !code[index] && index > 0) {
			inputRefs[index - 1].current?.focus();
		}
	};

	const handleSubmit = () => {
		const verificationCode = code.join('');
		onNext(verificationCode);
	};

	const formatPhone = (phone: string = '') => {
		const last2Digits = phone.slice(-2);
		return `(05**) *** ** ${last2Digits}`;
	};

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
	};

	return (
		<div className="verification-section">
			<div className="verification-header">
				<h2>Onay Kodunu Girin</h2>
				<div className="info">
					<i className="icons-info-circle" />
					<p>
						<b>{formatPhone(phone)}</b> numaralı telefon numaranıza SMS ile gönderilen 4 haneli kodu girin.
					</p>
				</div>
			</div>

			<div className={clsx('code-inputs', { wrong: verificationCodeStatusIsOk === false }, { success: verificationCodeStatusIsOk })}>
				{code.map((digit, index) => (
					<input
						key={index}
						ref={inputRefs[index]}
						type="text"
						inputMode="numeric"
						maxLength={1}
						value={digit}
						onChange={e => handleInput(index, e.target.value)}
						onKeyDown={e => handleKeyDown(index, e)}
						onPaste={index === 0 ? handlePaste : undefined}
						autoComplete="off"
					/>
				))}
			</div>

			{verificationCodeStatusIsOk === false && (
				<div className="wrong-code">
					<CircleAlert color="var(--danger-text)" />
					<p>Girdiğiniz kod yanlış, lütfen kontrol ediniz</p>
				</div>
			)}
			<div className="timer">
				{timeLeft === 0 && (
					<div className="resend">
						<p>Onay kodu gelmedi mi? </p>
						<button
							onClick={resendCode}
							disabled={sendVerificationCode.isLoading}
							className="resend-button">
							Tekrar SMS Gönder
						</button>
					</div>
				)}

				{timeLeft > 0 && (
					<Fragment>
						<Clock color="var(--brand-text-strong)" />
						<p className="blue-text">{formatTime(timeLeft)}</p>
					</Fragment>
				)}
			</div>

			<button
				className="verify-button"
				onClick={handleSubmit}
				disabled={code.some(digit => !digit) || disabled}>
				Onay Kodunu Gir
			</button>
		</div>
	);
};
